import React, { useEffect, useState } from "react";
import {
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";

import axiosInstance2 from "../../../api/axiosInstance2";
import {
    reportContainer,
    reportChartContainer,
    reportControls,
    reportControlButton,
    reportRange,
    reportStatus,
    reportTable,
    reportTableContainer,
} from "./report-common.module.scss";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface IReportNewsletterRow {
    date: string;
    newsletterName?: string | null;
    newsletterSentPerDate: number;
    openedNewsletterPerDate: number;
}

export default function ReportNewsletter() {
    const [isLoading, setIsLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");
    const [rows, setRows] = useState<IReportNewsletterRow[]>([]);
    const [offsetDays, setOffsetDays] = useState(0);

    useEffect(() => {
        const dateTo = new Date();
        dateTo.setDate(dateTo.getDate() + offsetDays);
        const dateFrom = new Date(dateTo);
        dateFrom.setDate(dateFrom.getDate() - 14);

        setIsLoading(true);
        setStatusMessage("");
        setRows([]);

        axiosInstance2
            .post("/Report/newsletter", {
                dateFrom: dateFrom.toISOString(),
                dateTo: dateTo.toISOString(),
            })
            .then((response) => {
                const payload = response?.data;
                const parsedRows = Array.isArray(payload)
                    ? payload
                    : Array.isArray(payload?.data)
                    ? payload.data
                    : [];

                setRows(parsedRows);
                if (parsedRows.length === 0) {
                    setStatusMessage("Brak danych dla wybranego zakresu dat.");
                }
            })
            .catch(() => {
                setStatusMessage("Nie udało się wygenerować raportu.");
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [offsetDays]);

    const formatDate = (value: string) => {
        if (!value) {
            return "-";
        }
        const parsedDate = new Date(value);
        if (Number.isNaN(parsedDate.getTime())) {
            return value;
        }
        return parsedDate.toLocaleDateString("pl-PL");
    };

    const getCurrentRangeLabel = () => {
        const dateTo = new Date();
        dateTo.setDate(dateTo.getDate() + offsetDays);
        const dateFrom = new Date(dateTo);
        dateFrom.setDate(dateFrom.getDate() - 14);
        return `${dateFrom.toLocaleDateString("pl-PL")} - ${dateTo.toLocaleDateString("pl-PL")}`;
    };

    const chartData = {
        labels: rows.map((row) => formatDate(row.date)),
        datasets: [
            {
                label: "Wysłane",
                data: rows.map((row) => row.newsletterSentPerDate),
                backgroundColor: "#c368a5",
                borderRadius: 6,
            },
            {
                label: "Otwarte",
                data: rows.map((row) => row.openedNewsletterPerDate),
                backgroundColor: "#7e5a76",
                borderRadius: 6,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "top" as const,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0,
                },
            },
        },
    };

    return (
        <div className={reportContainer}>
            <div className={reportControls}>
                <button
                    type="button"
                    className={reportControlButton}
                    onClick={() => setOffsetDays((prev) => prev - 14)}
                    disabled={isLoading}
                >
                    Wcześniej
                </button>
                <span className={reportRange}>{getCurrentRangeLabel()}</span>
                <button
                    type="button"
                    className={reportControlButton}
                    onClick={() => setOffsetDays((prev) => prev + 14)}
                    disabled={isLoading}
                >
                    Dalej
                </button>
            </div>
            {isLoading && <p className={reportStatus}>Generowanie raportu...</p>}
            {statusMessage && <p className={reportStatus}>{statusMessage}</p>}
            {!isLoading && rows.length > 0 && (
                <div className={reportChartContainer}>
                    <Bar data={chartData} options={chartOptions} />
                </div>
            )}
            <div className={reportTableContainer}>
                <table className={reportTable}>
                    <thead>
                        <tr>
                            <th>Data</th>
                            <th>Nazwa newslettera</th>
                            <th>Wysłane</th>
                            <th>Otwarte</th>
                        </tr>
                    </thead>
                    <tbody>
                        {!isLoading &&
                            rows.length > 0 &&
                            rows.map((row, index) => (
                                <tr key={`${row.date}-${row.newsletterName ?? ""}-${index}`}>
                                    <td>{formatDate(row.date)}</td>
                                    <td>{row.newsletterName?.trim() ? row.newsletterName : "—"}</td>
                                    <td>{row.newsletterSentPerDate}</td>
                                    <td>{row.openedNewsletterPerDate}</td>
                                </tr>
                            ))}
                        {!isLoading && rows.length === 0 && (
                            <tr>
                                <td colSpan={4}>Brak danych</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
